// src/components/DashboardShell.tsx
// REPOSITIONING PASS v2 — gate branch now renders ChatScreen in onboarding
// mode instead of OnboardingGate's single-field form, per the OAuth
// landing decision: new users (no entity/MoR yet) get Chat in onboarding
// mode; returning users land on Home. This depends on two things that are
// now real, not invented: ChatScreen accepts `onboarding` and
// `onEntityEstablished` props (see ChatScreen.tsx), and useDashboardChat
// exposes an `entityEstablished` flag when mode: 'onboarding' is used
// (see useDashboardChat.ts). The one remaining dependency is server-side:
// /dashboard/chat must actually return `entityEstablished: true` once its
// onboarding-mode entity-creation tool call resolves — not verified here,
// flagged as an open item.
//
// OnboardingGate.tsx itself is left in the codebase, untouched — not
// deleted, just no longer wired in as the default gate UI. Swap the
// import back if the Chat-based flow needs to be rolled back.
//
// SECTION_LABELS rebuilt to match the new sidebar's route set exactly.
// Old keys (qualification, vault) removed since those routes no longer
// exist as top-level destinations (see App.tsx — both now redirect
// rather than 404); new keys added for every new top-level route.
//
// UPDATED — SECTION_LABELS widened from Record<string, string> to
// Record<string, SectionInfo> to carry an optional group alongside each
// section's label, driving a two-level breadcrumb (group / section)
// once the sidebar grouped into Configuration/Activity/Record. Sections
// with no group (Home, Chat) render as a single breadcrumb item, same
// as before this change — non-breaking for anything ungrouped.
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UserProfile, ViewState } from '../types';
import { AppSidebar } from './app-sidebar';
import { SidebarInset, SidebarProvider, SidebarTrigger } from './ui/sidebar';
import { Separator } from './ui/separator';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { ChatScreen } from './dashboard/ChatScreen';
import { authedFetch } from '../services/dashboardApi';
// Add to the lucide-react import line:
import {
  Home, MessagesSquare, Package, LayoutDashboard, ShieldCheck,
  CheckSquare, FileCheck2, Store, BarChart3, Settings as SettingsIcon,
  SearchCode, Handshake,
} from 'lucide-react';

interface DashboardShellProps {
  user: UserProfile;
  onNavigate: (view: ViewState) => void;
}

interface SectionInfo {
  label: string;
  group?: string;
}

const SECTION_LABELS: Record<string, SectionInfo> = {
  '': { label: 'Home' },
  chat: { label: 'Chat' },

  profile: { label: 'Profile', group: 'Configuration' },
  outcomes: { label: 'Outcomes', group: 'Configuration' },
  rules: { label: 'Rules', group: 'Configuration' },
  settings: { label: 'Settings', group: 'Configuration' },

  timeline: { label: 'Timeline', group: 'Activity' },
  approvals: { label: 'Approvals', group: 'Activity' },
  detection: { label: 'Detection', group: 'Activity' },

  audit: { label: 'Audit & Proof', group: 'Record' },
  analytics: { label: 'Analytics', group: 'Record' },
};

export const DashboardShell: React.FC<DashboardShellProps> = ({ user, onNavigate }) => {
  const location = useLocation();
  const segments = location.pathname.replace('/dashboard', '').split('/').filter(Boolean);
  const currentSection = SECTION_LABELS[segments[0] || ''] ?? { label: 'Home' };

  const [hasEntity, setHasEntity] = useState<boolean | null>(null); // null = checking
  const [statusError, setStatusError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/entity-status');
        if (!res.ok) throw new Error('Failed to check account status.');
        const data = await res.json();
        if (!cancelled) setHasEntity(data.hasEntity);
      } catch (err: any) {
        if (!cancelled) setStatusError(err.message || 'Could not check account status.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (hasEntity === null && !statusError) {
    return (
      <div className="flex items-center justify-center min-h-screen text-sm text-brand-gray">
        Loading your account…
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="flex items-center justify-center min-h-screen text-sm text-red-600">
        {statusError}
      </div>
    );
  }

  // New user, no entity/MoR established yet — per the OAuth landing
  // decision, this is a full-screen Chat surface in onboarding mode,
  // not a structured form. onEntityEstablished flips hasEntity locally
  // once ChatScreen reports the backend has confirmed the entity now
  // exists, which drops straight into the normal sidebar shell below —
  // no page reload, no redirect round-trip.
  if (hasEntity === false) {
    return (
      <div className="flex h-screen w-full flex-col">
        <header className="shrink-0 border-b border-brand-border px-6 py-4">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-1">
            Set up your account
          </p>
          <p className="text-sm text-brand-charcoal font-light">
            Tell FDW about your business — this sets up your entity, your merchant-of-record status, and your first outcome.
          </p>
        </header>
        <div className="flex-1 min-h-0">
          <ChatScreen onboarding onEntityEstablished={() => setHasEntity(true)} />
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      {/* Outer Flex container locks to the viewport height and prevents page-level scrolling */}
      <div className="flex h-screen w-full overflow-hidden bg-background">
        <AppSidebar user={user} onNavigate={onNavigate} />

        {/* SidebarInset uses flex-1 to consume the remaining horizontal space automatically */}
        <SidebarInset className="flex w-full flex-1 flex-col overflow-y-auto bg-brand-surface/20">

          {/* Sticky header with z-index ensures timeline data scrolls underneath neatly */}
          <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {currentSection.group && (
                  <>
                    <BreadcrumbItem>
                      <span className="text-brand-gray">{currentSection.group}</span>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                )}
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-semibold">{currentSection.label}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>

          {/* Main content wrapper */}
          <main className="mx-auto w-full max-w-6xl flex-1 space-y-6 p-4 md:p-8">
            <Outlet context={{ user }} />
          </main>

        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};